import type { InsuranceType, Passenger, PaxType, PriceGroup, ShoppingCart, TripType } from '@optionway/models';
import { modelsImpl, PaxTypeEnum } from '@optionway/models';

export const useShoppingCartStore = defineStore('shopping-cart', {
  getters: {
    shoppingCart(): ShoppingCart {
      const selectedOfferStore = useSelectedOfferStore();
      const passengersStore = usePassengersStore();
      const billingStore = useBillingStore();
      const currencyStore = useCurrencyStore();
      const selectedInsuranceStore = useSelectedInsuranceStore();
      const selectedContributionStore = useSelectedContributionStore();
      const luggagesStore = useLuggagesStore();
      const discountStore = useDiscountStore();
      const partnerDataStore = usePartnerDataStore();

      const cart: ShoppingCart = new modelsImpl.ShoppingCartImpl();
      const flight = selectedOfferStore.offer?.flight;
      if (!flight) {
        // Shopping cart is void if no flight pricing
        return cart;
      }
      cart.flight = flight;
      cart.includedBags = selectedOfferStore.offer?.includedBags ?? [];
      cart.passengers = passengersStore.passengers;
      cart.selectedBags = luggagesStore.selectedBagsForAll;
      cart.insurance = selectedInsuranceStore.insurance;
      cart.contribution = selectedContributionStore.contribution;
      cart.billing = billingStore.billing;
      cart.partnerData = partnerDataStore.partnerData;
      cart.payment = {
        amountInEuro:
          (selectedOfferStore.offer?.flight?.price?.total ?? 0) +
          (selectedInsuranceStore.insurance?.price.total ?? 0) +
          (selectedContributionStore.contribution?.price ?? 0) +
          luggagesStore.selectedBagsTotalPrice -
          (discountStore.discount?.amount ?? 0),
        currency: currencyStore.currency,
        currencyRate: 1,
        cardType: 'CB',
        discount: discountStore.discount,
      };
      return cart;
    },
    amountToPay(): number {
      return this.shoppingCart.payment.amountInEuro ?? 0;
    },
    amountToPayInCents(): number {
      return Math.round(this.amountToPay * 100);
    },
    passengerPriceItems(): Partial<Record<PaxType, PriceGroup>> {
      const price = this.shoppingCart.flight?.price;
      if (!price) return {};
      const result: Partial<Record<PaxType, PriceGroup>> = {};
      if (isPaxGroupPresent(price.adultGroup)) result[PaxTypeEnum.ADULT] = price.adultGroup;
      if (isPaxGroupPresent(price.childGroup)) result[PaxTypeEnum.CHILD] = price.childGroup;
      if (isPaxGroupPresent(price.infantGroup)) result[PaxTypeEnum.INFANT] = price.infantGroup;
      return result;
    },
    hasPassengerPriceItems(): boolean {
      return Object.keys(this.passengerPriceItems).length > 0;
    },
    selectedInsurancePriceItem(): { type: InsuranceType; total: number } | undefined {
      const selectedInsuranceStore = useSelectedInsuranceStore();
      const type = selectedInsuranceStore.knownInsuranceType;
      if (!type) return undefined;
      return { type, total: selectedInsuranceStore.insurance?.price.total ?? 0 };
    },
    hasSelectedInsurance(): boolean {
      return this.shoppingCart.insurance !== undefined;
    },
    hasSelectedContribution(): boolean {
      return this.shoppingCart.contribution !== undefined;
    },
    hasDiscount(): boolean {
      return this.shoppingCart.payment.discount !== undefined;
    },
    isFullContribution(): boolean {
      return useSelectedContributionStore().isFullContribution;
    },
    selectedOptionsCount(): number {
      return Number(this.hasSelectedInsurance) + Number(this.hasSelectedContribution);
    },
    hasSelectedOptions(): boolean {
      return this.selectedOptionsCount > 0;
    },
    departureDateTime(): string | undefined {
      return this.shoppingCart.flight?.legs[0]?.departureDateTime;
    },
    returnDateTime(): string | undefined {
      const legs = this.shoppingCart.flight?.legs;
      return legs && legs.length > 1 ? legs.at(-1)?.departureDateTime : undefined;
    },
    passengersGroupedByType(): Partial<Record<PaxType, Passenger[]>> {
      return usePassengersStore().groupedByType;
    },
    passengerCount(): number {
      return this.shoppingCart.passengers.length;
    },
    hasFilledPassenger(): boolean {
      return this.shoppingCart.passengers.some(
        (p) =>
          !!p.lastName ||
          !!p.firstName ||
          !!p.birthdate ||
          !!p.contact?.email ||
          !!p.contact?.phoneCode?.description?.trim() ||
          !!p.contact?.phoneNumber?.trim(),
      );
    },
    tripType(): TripType | undefined {
      return this.shoppingCart.flight.tripType;
    },
    // Until a dedicated city name is available from the BFF, exposes the arrival IATA code of the first leg as the destination label.
    destinationCityIataCode(): string {
      return this.shoppingCart.flight?.legs[0]?.arrival?.cityIataCode ?? '';
    },
  },
});

function isPaxGroupPresent(group: PriceGroup | undefined): group is PriceGroup {
  return !!group && ((group.count ?? 0) > 0 || (group.paxDetail?.total ?? 0) > 0);
}
